import { useState } from 'react';
import { createPortal } from 'react-dom';
import Modal from './Modal';
import { formatPlanDateInZone, getSubscriptionState } from '../utils/subscriptionDateUtil';
import { setClientDisabled } from '../utils/posSubscriptionUtil';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import { POS_SUBSCRIPTION_STATUS } from '../constants/posConst';
import '../css/PosAdmin.css';
import '../css/PosSubscription.css';

/**
 * Disable / Enable for one client's till, on its admin card.
 *
 * An EXPIRED plan locks the till by itself (PosExpiredLock), so this is the
 * manual override on top: shut any client's till whatever its plan says, and
 * open it again. Both ask first, and both are recorded on the client's trail
 * and the administrators' trail.
 *
 * @param {object} props
 * @param {object} props.client - from subscribePosClients (id, name, subscription, access, timezone)
 * @param {string} props.signedInEmail - recorded as who did it
 * @returns {JSX.Element|null}
 */
function PosClientAccessControl({ client, signedInEmail }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const disabled = client.access?.disabled === true;
  const expired = getSubscriptionState(client.subscription, client.timezone).status === POS_SUBSCRIPTION_STATUS.EXPIRED;

  const planLabel = client.subscription?.isTrial ? 'free trial' : 'subscription';
  const endedOn = formatPlanDateInZone(client.subscription?.endDate, client.timezone);

  /** Applies the change the confirmation was opened for. @returns {Promise<void>} */
  const handleConfirm = async () => {
    setConfirming(false);
    setBusy(true);
    try {
      await setClientDisabled(client, !disabled, signedInEmail);
    } catch (writeError) {
      console.error('Changing client access failed:', writeError);
      setError(describeWriteError(writeError, { what: 'this change', path: `posClients/${client.id}/access` }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {disabled && (
        <p className="ps-access-note">
          Till disabled{client.access?.disabledAt ? ` on ${new Date(client.access.disabledAt).toLocaleDateString()}` : ''}.
          Staff see the &ldquo;access paused&rdquo; screen until it is enabled or a renewal is approved.
        </p>
      )}
      <button
        type="button"
        className={`pa-btn ${disabled ? 'pa-btn-primary' : 'ps-btn-danger-quiet'} ps-access-btn${!disabled && !expired ? ' ps-access-btn-subtle' : ''}`}
        onClick={() => setConfirming(true)}
        disabled={busy}
      >
        {busy ? 'Saving…' : (disabled ? 'Enable till' : 'Disable till')}
      </button>

      {/* Portalled to <body>: the card this sits in lifts on hover with a
          transform, and a transformed ancestor traps position:fixed — the
          dialog would otherwise be squeezed inside the card. */}
      {createPortal(
        <>
          <Modal
            isOpen={confirming}
            onClose={() => setConfirming(false)}
            title={disabled ? `Enable ${client.name || client.id}?` : `Disable ${client.name || client.id}?`}
            message={disabled
              ? `Their till opens again straight away.${expired ? ` Their ${planLabel} is still expired, so it will stay locked by the expiry screen until they renew or you extend the end date on their Subscriptions tab.` : ''}`
              : `${expired
                ? `Their ${planLabel} ended on ${endedOn}, so their till is already locked automatically. Disabling it keeps it locked even if you extend the plan, until you enable it again or approve a renewal.`
                : `Their ${client.subscription ? `${planLabel} is still active` : 'till has no subscription'}, but every till for this client will be locked straight away, including anyone signed in right now.`} Their products, sales and settings are kept.`}
            type={disabled ? 'info' : 'error'}
            showCancel
            confirmText={disabled ? 'Enable till' : 'Disable till'}
            onConfirm={handleConfirm}
          />

          <Modal
            isOpen={Boolean(error)}
            onClose={() => setError(null)}
            title="Could not save"
            message={error || ''}
            type="error"
          />
        </>,
        document.body,
      )}
    </>
  );
}

export default PosClientAccessControl;
